import React from "react";
import { useStore } from "@thekrystalship/krystal";
import { askAssistant, useAssistantSurface } from "@thekrystalship/krystal-chat";
import { assistantSession } from "../lib/assistantSession.js";
import { fmtRelative, parseTs } from "../lib/formatting.js";
import { ASSISTANT_HOST, assistantStore, serversStore } from "../lib/stores.js";
import { useOnline } from "../hooks/useOnline.js";

// AssistantDockContext — extracts the assistant dock state, derived layout
// values, and interaction functions out of App.jsx. The provider owns all
// dock-related state; consumers read via useAssistantDock().

const AssistantDockContext = React.createContext(null);

function useAssistantDock() {
  return React.useContext(AssistantDockContext);
}

// useAssistant() — the assistant that would answer a question now, or null.
//
// The ONE gate behind every "ask the assistant" affordance: the build names an assistant and its
// plugin answers `/health`. A button that offers to ask and the dock that would answer read the same
// store, so they cannot disagree about whether there is an assistant.
function useAssistant() {
  const state = useStore(assistantStore, (s) => s.state);
  return state === "connected" ? ASSISTANT_HOST : null;
}

function alertAssistantPrompt(item) {
  if (!item) return "";
  const srv = item.serverId ? serversStore.find(item.serverId) : null;
  const where = srv ? " on " + srv.name : "";
  const when = item.raisedAt
    ? fmtRelative(parseTs(item.raisedAt), new Date())
    : null;
  const raised = when ? " (raised " + when + ")" : "";
  if (item.escalated) {
    return "The alert “" + item.title + "”" + where + " escalated — auto-recovery gave up after "
      + item.attempts + " attempt" + (item.attempts === 1 ? "" : "s") + " and it needs a human. "
      + "Walk me through diagnosing and fixing it.";
  }
  return "The alert “" + item.title + "” is firing" + where + raised
    + ". What's likely causing it, and how do I fix it?";
}

function AssistantDockProvider({ route, setRoute, children }) {
  // The assistant this build names, or null when it names none: every entry point to the chat is
  // absent then. Whether the plugin answers is `assistantUsable`, which the palette offers "ask the
  // assistant" off, so a plugin that does not answer hides an action that could only fail.
  const assistantHost = assistantSession.hasRoute() ? ASSISTANT_HOST : null;
  const assistantUsable = useStore(assistantStore, (s) => s.state) === "connected";

  // ===== The surface =====
  // The dock's frame (open, width, pin, the push-or-float decision), the one chat that moves between
  // the dock and the assistant's page, the launcher and a request to ask are the design system's. The
  // dock comes back the way it was left and opens, on a wide screen, for somebody who has never closed
  // it; on a phone the assistant is a page, and every way of asking for it goes there.
  // Offline (the entry node does not answer) the dock holds no chat and the launcher is not drawn.
  const online = useOnline();
  const surface = useAssistantSurface({
    storageKey: "krystal:dock",
    contentFloor: 1000,
    available: !!assistantHost,
    onPage: route.kind === "assistant",
    openPage: () => setRoute({ kind: "assistant" }),
    autoOpen: true,
    framePage: "phone",
    online,
  });
  const { dock } = surface;

  // ===== Functions =====
  const handleAssistantNavigate = React.useCallback((target) => {
    if (!target) return;
    if (target.kind === "server") setRoute({ kind: "server", id: target.serverId, tab: target.tab });
    else if (target.kind === "audit") setRoute({ kind: "audit" });
    else setRoute({ kind: target.kind === "diagnostics" ? "fleet" : target.kind });
  }, [setRoute]);

  const openView = React.useCallback((view) => {
    setRoute({ kind: view === "diagnostics" ? "fleet" : view });
  }, [setRoute]);

  const { open: openAssistant } = surface;
  const desktop = dock.desktop;

  // Put an alert in the composer as a question to finish, with the server's page behind it on a wide
  // screen. The text is editable and not sent — the user reads and sends it, we never speak for them.
  const askAboutAlert = React.useCallback((item) => {
    if (!item) { openAssistant(); return; }
    if (item.serverId && desktop) setRoute({ kind: "server", id: item.serverId });
    askAssistant(alertAssistantPrompt(item));
  }, [desktop, setRoute, openAssistant]);

  // Hand a blueprint-authoring request to the assistant, from the create page. On a wide screen the
  // INLINE dock opens and the page stays put; on a phone the assistant page opens and the create page
  // keeps its draft (pages/library/LibraryCreatePage.jsx). Either way the prompt is in the composer,
  // editable and not sent.
  const askCreateBlueprint = React.useCallback((gameName) => {
    askAssistant("Create a blueprint for " + (gameName || ""));
  }, []);

  // ===== Context value =====
  const value = React.useMemo(() => ({
    surface, dock,
    assistantHost, assistantUsable,
    handleAssistantNavigate, openView,
    askAssistant, askAboutAlert, askCreateBlueprint, openAssistant,
  }), [
    surface, dock,
    assistantHost, assistantUsable,
    handleAssistantNavigate, openView,
    askAboutAlert, askCreateBlueprint, openAssistant,
  ]);

  return (
    <AssistantDockContext.Provider value={value}>
      {children}
    </AssistantDockContext.Provider>
  );
}

export { AssistantDockProvider, useAssistantDock, useAssistant, alertAssistantPrompt };
